import { useCallback } from "react"
import useApi from "./useApi"
import { TenantSubDomain } from "mco-tenant-configurations"
import { toast } from "react-toastify"
import { useDispatch, useSelector } from "react-redux"
import { environmentSlice } from "../../state/environmentSlice"
import { DateTime } from "luxon"
import { zoneRegistry } from "mco-zone-configurations"

export const useAnonymizeTenant = () => {
	const api = useApi()

	const send = useCallback(
		async (confirmation: TenantSubDomain) => {
			try {
				await api.environment.anonymiseTenant(confirmation)
				toast.success("Started anonymise. Check Slack for progress")
			} catch {
				toast.error(
					"Something went wrong starting the anonymise process"
				)
			}
		},
		[api.environment]
	)

	return send
}

export const useEnableRdsKeepAlive = () => {
	const api = useApi()
	const dispatch = useDispatch()
	const isEnabled = useSelector(
		environmentSlice.selectors.rdsKeepAliveEnabled
	)

	const action = useCallback(async () => {
		try {
			await api.environment.enableRdsKeepAlive()
			dispatch(environmentSlice.actions.setRdsKeepAliveEnabled())
			toast.success("Enabled RDS keep alive")
		} catch {
			toast.error("Something went wrong enabling keep alive")
		}
	}, [api.environment, dispatch])

	return [isEnabled, action] as const
}

export const useSetOverriddenDate = () => {
	const dispatch = useDispatch()
	const set = useCallback(
		(overriddenDate?: DateTime<true> | string) => {
			if (zoneRegistry.isProduction()) {
				throw new Error("Cannot time travel in production")
			}

			if (!overriddenDate) {
				dispatch(environmentSlice.actions.setOverrideDate(undefined))
				return
			}

			if (typeof overriddenDate === "string") {
				const date = DateTime.fromISO(overriddenDate)
				if (!date.isValid) {
					throw new Error(
						`Cannot time travel to invalid date '${overriddenDate}'`
					)
				}
				dispatch(environmentSlice.actions.setOverrideDate(date))
				return
			}

			dispatch(environmentSlice.actions.setOverrideDate(overriddenDate))
		},
		[dispatch]
	)
	return set
}

export const useOverriddenDateRaw = () =>
	useSelector(environmentSlice.selectors.overriddenDateRaw)
